import { Checkmark } from '@carbon/icons-react';
import type { SelectOption } from '@ph-react-ui/core';
import { Button, Table, Tag, Tooltip } from '@ph-react-ui/core';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/auth/useAuth';
import { useGetMonthlyMenuByStudent } from '../../../hooks/payments/useGetMonthlyMenuByStudent';
import { useGetYearlyMenuPricePlan } from '../../../hooks/payments/useGetYearlyMenuPricePlan';
import { useGetAuthenticatedParentProfile } from '../../../hooks/profile/parent/useGetAuthenticatedParentProfile';
import { useModal } from '../../../hooks/useModal';
import { useGetStudentsForParent } from '../../../hooks/users/parents/useGetStudentsForParent';
import { Scholarship } from '../../../models/users/students/scholarship-type';
import { Select } from '../../../utils/components/hoc-components';
import { SCHOOL_YEAR_START_MONTH } from '../../../utils/constants/food-management/schoolYearStartMonth';
import { PARTIAL_MONTHLY_MENU_PAYMENT } from '../../../utils/constants/payments/modals';
import { displayPrice } from '../../../utils/displayPrice';
import { Currency } from '../../../utils/enums/currency-type.enum';
import { ScopeAccess } from '../../../utils/enums/scope-access.enum';
import { EmptyState } from '../../shared/components/EmptyState';
import { PageTitle } from '../../shared/components/PageTitle';
import { TableSkeleton } from '../../shared/components/skeletons/TableSkeleton';

export const ParentMonthlyMenuPayments = () => {
  const { authState } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const methods = useForm();
  const { open: openPartialPaymentModal } = useModal(
    PARTIAL_MONTHLY_MENU_PAYMENT
  );

  const [studentOptions, setStudentOptions] = useState<SelectOption<string>[]>(
    []
  );

  const canMakePayments = useMemo(
    () => authState?.scope?.includes(ScopeAccess.MakePayments),
    [authState?.scope]
  );

  const selectedStartYear = useMemo(() => {
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();

    return currentMonth >= SCHOOL_YEAR_START_MONTH
      ? currentYear
      : currentYear - 1;
  }, []);

  const { data: parent } = useGetAuthenticatedParentProfile();
  const { data: students } = useGetStudentsForParent({
    enabled: Boolean(parent),
  });

  const studentIdParam = useMemo(
    () => searchParams.get('studentId') ?? studentOptions?.[0]?.value,
    [searchParams, studentOptions]
  );

  const { data: monthlyMenuItems, isLoading: isLoadingItems } =
    useGetMonthlyMenuByStudent(studentIdParam, selectedStartYear);

  const { data: yearlyPricePlan } =
    useGetYearlyMenuPricePlan(selectedStartYear);

  useEffect(() => {
    if (!students) return;

    const options = students.map((student) => ({
      label: student.name,
      value: student.id,
    }));
    setStudentOptions(options);

    const hasSelectedStudent =
      Boolean(studentIdParam) &&
      options.some((student) => student.value === studentIdParam);

    if ((!studentIdParam || !hasSelectedStudent) && options.length > 0) {
      searchParams.set('studentId', options[0].value);
      setSearchParams(searchParams);
    }
  }, [students, studentIdParam, searchParams, setSearchParams]);

  useEffect(() => {
    const yearParam = searchParams.get('year');
    const monthParam = searchParams.get('month');

    if (yearParam || monthParam) {
      if (yearParam) searchParams.delete('year');
      if (monthParam) searchParams.delete('month');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const selectedStudent = useMemo(() => {
    if (!studentIdParam || !students) return undefined;

    const student = students.find((s) => s.id === studentIdParam);
    if (!student) return undefined;

    const yearlyPlan = yearlyPricePlan?.pricePlans?.find(
      (plan) => plan.id === studentIdParam
    );

    if (yearlyPlan) {
      return {
        id: student.id,
        name: student.name,
        totalAmount: yearlyPlan.totalAmount,
        paidAmount: yearlyPlan.paidAmount,
        remainingAmount: yearlyPlan.remainingAmount,
        currency: yearlyPlan.currency,
        scholarshipType: student.scholarshipType,
        items: monthlyMenuItems ?? [],
        remainingInstallments: yearlyPlan.remainingInstallments,
      };
    }

    if (!monthlyMenuItems) return undefined;

    const totalAmount = monthlyMenuItems.reduce(
      (sum: number, item) => sum + item.amount,
      0
    );
    const paidAmount = monthlyMenuItems
      .filter((item) => item.paidAt)
      .reduce((sum: number, item) => sum + item.amount, 0);

    return {
      id: student.id,
      name: student.name,
      totalAmount,
      paidAmount,
      remainingAmount: totalAmount - paidAmount,
      currency: monthlyMenuItems[0]?.currency ?? Currency.EUR,
      scholarshipType: student.scholarshipType,
      items: monthlyMenuItems,
      remainingInstallments: monthlyMenuItems.filter((item) => !item.paidAt)
        .length,
    };
  }, [studentIdParam, students, monthlyMenuItems, yearlyPricePlan]);

  const onSelectStudent = useCallback(
    (value: string | number | (string | number)[] | null) => {
      if (!value) return;

      searchParams.set('studentId', value.toString());
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const onNavigateToFullPayment = useCallback(() => {
    if (!selectedStudent) return;

    const unpaidItems = selectedStudent.items.filter((item) => !item.paidAt);
    const mealMenuIds = unpaidItems.map((item) => item.mealMenuId);
    const paymentItems = unpaidItems.map((item) => ({
      mealMenuId: item.mealMenuId,
      month: item.month,
      year: item.year,
      amount: item.amount,
      currency: item.currency,
    }));
    const firstUnpaidItem = unpaidItems[0];
    navigate(
      `/dashboard/payments/monthly-menu/pre-payment/${selectedStudent.id}/${firstUnpaidItem.month}/${firstUnpaidItem.year}`,
      { state: { mealMenuIds, paymentItems } }
    );
  }, [selectedStudent, navigate]);

  const onOpenPartialPayment = useCallback(() => {
    if (!selectedStudent) return;
    openPartialPaymentModal({
      id: selectedStudent.id,
      name: selectedStudent.name,
    });
  }, [selectedStudent, openPartialPaymentModal]);

  const onPayMonthlyItem = useCallback(
    (
      mealMenuId: string,
      month: number,
      year: number,
      amount: number,
      currency: Currency
    ) => {
      if (!selectedStudent) return;

      navigate(
        `/dashboard/payments/monthly-menu/pre-payment/${selectedStudent.id}/${month}/${year}`,
        {
          state: {
            mealMenuIds: [mealMenuId],
            paymentItems: [{ mealMenuId, month, year, amount, currency }],
          },
        }
      );
    },
    [selectedStudent, navigate]
  );

  const isOnFoodScholarship = useMemo(
    () => selectedStudent?.scholarshipType === Scholarship.FULL_SCHOLARSHIP,
    [selectedStudent?.scholarshipType]
  );

  const headers = useMemo(
    () => [
      t('PAYMENTS.TABLE.HEADERS.MONTH'),
      t('PAYMENTS.TABLE.HEADERS.AMOUNT'),
      t('PAYMENTS.TABLE.HEADERS.PAID_BY'),
      t('PAYMENTS.TABLE.HEADERS.STATUS'),
      t('PAYMENTS.TABLE.HEADERS.ACTIONS'),
    ],
    [t]
  );

  const tableChildren = useMemo(() => {
    if (!selectedStudent?.items) return [];

    const sortedItems = [...selectedStudent.items].sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });

    const unpaidItems = sortedItems.filter((item) => !item.paidAt);
    const firstUnpaidMonth =
      unpaidItems.length > 0
        ? new Date(unpaidItems[0].year, unpaidItems[0].month - 1)
        : null;

    return sortedItems.map((item) => {
      const itemDate = new Date(item.year, item.month - 1);
      const monthName = t(`MONTHS.${format(itemDate, 'MMMM').toUpperCase()}`);
      const isPaid = Boolean(item.paidAt);

      // Can only pay if it's the oldest unpaid month
      const isPayEnabled =
        !isPaid &&
        firstUnpaidMonth &&
        itemDate.getTime() === firstUnpaidMonth.getTime();

      const tooltipMessage = isPaid
        ? t('FOOD_MANAGEMENT.MESSAGES.ALREADY_PAID')
        : !isPayEnabled && canMakePayments
        ? t('PAYMENTS.TABLE.LABELS.PAY_PREVIOUS_FIRST')
        : '';

      const payButton = (
        <Button
          variant="outlined"
          disabled={!isPayEnabled || !canMakePayments}
          onClick={() =>
            onPayMonthlyItem(
              item.mealMenuId,
              item.month,
              item.year,
              item.amount,
              item.currency
            )
          }
        >
          {t('PAYMENTS.TABLE.ACTIONS.PAY')}
        </Button>
      );

      return [
        `${monthName} ${item.year}`,
        displayPrice(
          item.amount,
          t('PAYMENTS.LABELS.BGN'),
          t('PAYMENTS.LABELS.EUR'),
          item.currency
        ),
        isPaid ? item.paidByName || '-' : '-',
        isPaid ? (
          <Tag type="success">{t('TAXES.STATUS.PAID')}</Tag>
        ) : (
          <Tag type="accent">{t('TAXES.STATUS.PENDING')}</Tag>
        ),
        tooltipMessage ? (
          <Tooltip position="left" message={tooltipMessage}>
            {payButton}
          </Tooltip>
        ) : (
          payButton
        ),
      ];
    });
  }, [selectedStudent, t, canMakePayments, onPayMonthlyItem]);

  const schoolYearSubtitle = `${t(
    'PAYMENTS.LABELS.SCHOOL_YEAR'
  )} ${selectedStartYear} -
    ${selectedStartYear + 1}`;

  if (isLoadingItems) {
    return <TableSkeleton />;
  }

  return (
    <FormProvider {...methods}>
      <div className="payments">
        <PageTitle
          title={t('PAYMENTS.TITLES.MONTHLY_MENU')}
          subtitle={schoolYearSubtitle}
        />
        <div className="school-taxes__actions">
          <Select
            name="student"
            compact
            placeholder={t('TAXES.LABELS.CHOOSE_STUDENT') ?? ''}
            label={t('TAXES.LABELS.CHOOSE_STUDENT') ?? ''}
            options={studentOptions}
            value={studentIdParam}
            onChange={(value) => onSelectStudent(value)}
          />
        </div>
        {selectedStudent && selectedStudent.items.length > 0 && (
          <div className="school-taxes__filter">
            <div className="school-taxes__filter__info">
              <div className="school-taxes__filter__row">
                <div className="school-taxes__filter__item">
                  <span className="school-taxes__filter__label">
                    {t('PAYMENTS.TABLE.HEADERS.TOTAL_AMOUNT')}:
                  </span>
                  <span className="school-taxes__filter__value">
                    {displayPrice(
                      selectedStudent.totalAmount,
                      t('PAYMENTS.LABELS.BGN'),
                      t('PAYMENTS.LABELS.EUR'),
                      selectedStudent.currency
                    )}
                  </span>
                </div>
                <div className="school-taxes__filter__item">
                  <span className="school-taxes__filter__label">
                    {t('PAYMENTS.TABLE.HEADERS.PAID_AMOUNT')}:
                  </span>
                  <span className="school-taxes__filter__value">
                    {selectedStudent.paidAmount >= 0
                      ? displayPrice(
                          selectedStudent.paidAmount,
                          t('PAYMENTS.LABELS.BGN'),
                          t('PAYMENTS.LABELS.EUR'),
                          selectedStudent.currency
                        )
                      : 0}
                  </span>
                </div>
                <div className="school-taxes__filter__item">
                  <span className="school-taxes__filter__label">
                    {t('PAYMENTS.TABLE.HEADERS.PENDING_AMOUNT')}:
                  </span>
                  <span className="school-taxes__filter__value">
                    {displayPrice(
                      selectedStudent.remainingAmount,
                      t('PAYMENTS.LABELS.BGN'),
                      t('PAYMENTS.LABELS.EUR'),
                      selectedStudent.currency
                    )}
                  </span>
                </div>
              </div>
              <div className="school-taxes__filter__row school-taxes__filter__row--full">
                <div className="school-taxes__filter__item">
                  <span className="school-taxes__filter__label">
                    {t('PAYMENTS.TABLE.HEADERS.NOTES')}:
                  </span>
                  <span className="school-taxes__filter__value">
                    {isOnFoodScholarship ? (
                      t('PAYMENTS.TABLE.LABELS.ON_SCHOLARSHIP')
                    ) : selectedStudent.totalAmount ===
                      selectedStudent.paidAmount ? (
                      <span className="paid-tax">
                        <Checkmark size={'16'} />{' '}
                        {t('PAYMENTS.TABLE.LABELS.TAX_PAID')}
                      </span>
                    ) : selectedStudent.remainingAmount ? (
                      t('PAYMENTS.TABLE.LABELS.REVIEW_PAYMENTS')
                    ) : (
                      '-'
                    )}
                  </span>
                </div>
              </div>
            </div>
            {canMakePayments &&
              !isOnFoodScholarship &&
              selectedStudent.totalAmount !== selectedStudent.paidAmount &&
              selectedStudent.remainingAmount > 0 &&
              selectedStudent.remainingInstallments > 0 && (
                <div className="school-taxes__filter__actions">
                  <Button onClick={onOpenPartialPayment}>
                    {t('PAYMENTS.TABLE.ACTIONS.PAY_PARTIALLY')}
                  </Button>
                  <Button onClick={onNavigateToFullPayment}>
                    {t('PAYMENTS.TABLE.ACTIONS.PAY_FULL')}
                  </Button>
                </div>
              )}
          </div>
        )}

        {isLoadingItems && <TableSkeleton />}
        {!isLoadingItems && selectedStudent && (
          <>
            {tableChildren.length > 0 ? (
              <div className="school-taxes__installments-table">
                <Table headers={headers} rows={tableChildren} />
              </div>
            ) : (
              <EmptyState />
            )}
          </>
        )}
      </div>
    </FormProvider>
  );
};
